// src/components/modules/EnhancedModuleCard.jsx
import React from 'react';
import { motion } from 'framer-motion';
import {
  Clock,
  CheckCircle,
  Lock,
  BookOpen,
  Target,
  Lightbulb,
  Play,
  PlayCircle
} from 'lucide-react';
import { getModuleById } from '../../data/modules';

// Prerequisites are stored as module ids, so they have to be resolved to titles
// before display. Every outstanding one is listed: a module can require three,
// and naming only the first implies finishing it alone would unlock the module.
const formatPrerequisites = (prerequisiteIds) => {
  const titles = prerequisiteIds.map((id) => getModuleById(id)?.title || id);
  if (titles.length === 1) return titles[0];
  return `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
};

const EnhancedModuleCard = ({ module, status, onClick, moduleIndex = 0 }) => {
  // Determine if module is unlocked/clickable
  const isClickable = status !== 'locked';
  const isComplete = status === 'completed';
  const isCurrent = status === 'inProgress' || status === 'available';

  // Mirrors the CTA's visible text so each card's button is distinguishable
  // to a screen reader ("Start Module — Ethics", not eight bare "Start Module"s).
  const ctaLabel = isComplete
    ? 'Review'
    : isCurrent
    ? (status === 'inProgress' ? 'Continue Learning' : 'Start Learning')
    : isClickable
    ? 'Start Module'
    : 'Locked';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: moduleIndex * 0.05, duration: 0.5 }}
      whileHover={{ y: isClickable ? -4 : 0 }}
      className="h-full"
    >
      {/* Locked state is carried by the Lock icon and the disabled CTA, not by
          dimming the whole card: card-level opacity took the description text
          to 1.7:1 (same trap as the old Lineage unmet nodes). */}
      <div
        aria-disabled={!isClickable}
        className={`group relative h-full transition-all duration-300 ${
          isClickable ? 'cursor-pointer hover:shadow-2xl hover:shadow-indigo-500/10' : 'cursor-not-allowed'
        } ${
          isCurrent ? 'ring-2 ring-indigo-500 shadow-lg shadow-indigo-500/20' : ''
        } bg-slate-900/80 border border-slate-800 backdrop-blur-sm overflow-hidden hover:border-slate-700 rounded-xl`}
        onClick={() => isClickable && onClick && onClick()}
      >
        {/* Module Image Header */}
        <div className="relative h-40 -mx-px -mt-px">
          <img
            src={module.heroImage || module.image || `/images/cloud-${String(module.order || moduleIndex + 1).padStart(2, '0')}.webp`}
            alt={module.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = document.createElement('div');
              fallback.className = 'w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center';
              fallback.innerHTML = '<div class="text-slate-400 text-4xl">📚</div>';
              e.target.parentNode.appendChild(fallback);
            }}
          />

          {/* Lighter gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />

          {/* Module number */}
          <div className="absolute top-3 left-3 w-8 h-8 bg-slate-900/90 backdrop-blur-sm rounded-lg flex items-center justify-center border border-slate-700">
            <span className="text-white font-bold text-sm">{module.order || moduleIndex + 1}</span>
          </div>

          {/* Status icon */}
          <div className="absolute top-3 right-3">
            {isComplete ? (
              <div className="p-1.5 bg-violet-500/20 rounded-lg backdrop-blur-sm">
                <CheckCircle className="w-5 h-5 text-violet-400" />
              </div>
            ) : isCurrent ? (
              <div className="p-1.5 bg-indigo-500/20 rounded-lg backdrop-blur-sm">
                <Play className="w-5 h-5 text-indigo-400" />
              </div>
            ) : !isClickable ? (
              <div className="p-1.5 bg-slate-800/80 rounded-lg backdrop-blur-sm">
                <Lock className="w-5 h-5 text-slate-400" />
              </div>
            ) : null}
          </div>

          {/* Difficulty badge */}
          <div className="absolute bottom-3 left-3">
            <span className={`px-2 py-1 text-xs font-medium rounded-md backdrop-blur-sm border ${
              module.difficulty === 'Foundation' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
              module.difficulty === 'Essential' ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :
              'bg-rose-500/20 text-rose-300 border-rose-500/30'
            }`}>
              {module.difficulty}
            </span>
          </div>
        </div>

        <div className="p-5">
          {/* Title & Description */}
          <div className="mb-3">
            <h3 className="text-lg font-bold text-white mb-1.5 group-hover:text-indigo-400 transition-colors leading-tight">
              {module.title}
            </h3>
            <p className="text-slate-400 text-sm line-clamp-2 leading-relaxed">
              {module.description}
            </p>
          </div>

          {/* Quick Stats */}
          <div className="flex items-center gap-3 mb-3 text-xs text-slate-400">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{module.duration || '45 mins'}</span>
            </div>
            <div className="w-1 h-1 bg-slate-600 rounded-full" />
            <div className="flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              <span>{module.content?.sections?.length || module.lessons?.length || module.sections?.length || 5} lessons</span>
            </div>
          </div>

          {/* Progress Bar (if in progress or completed) */}
          {(status === 'inProgress' || status === 'completed') && (
            <div className="mb-3">
              <div className="w-full bg-slate-800 rounded-full h-1 overflow-hidden">
                <div
                  className={`h-1 rounded-full transition-all duration-500 ${
                    isComplete ? 'bg-violet-400' : 'bg-gradient-to-r from-indigo-500 to-violet-500'
                  }`}
                  style={{ width: `${module.progress || (isComplete ? 100 : 50)}%` }}
                />
              </div>
            </div>
          )}

          {/* Key Insight Preview */}
          {module.objectives && module.objectives.length > 0 && (
            <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-300 leading-relaxed line-clamp-2">
                  {module.objectives[0]}
                </p>
              </div>
            </div>
          )}

          {/* Prerequisite Info for Locked Modules */}
          {!isClickable && module.prerequisites && module.prerequisites.length > 0 && (
            <div className="mb-3 p-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <p className="text-xs text-amber-300 text-center">
                Complete <span className="font-semibold">{formatPrerequisites(module.prerequisites)}</span> to unlock
              </p>
            </div>
          )}

          {/* CTA Button */}
          <button
            aria-label={`${ctaLabel}: ${module.title}`}
            className={`w-full py-2.5 px-3 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
              isComplete
                ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20'
                : isCurrent
                ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:shadow-lg hover:shadow-indigo-500/25 transform hover:scale-[1.02]'
                : isClickable
                ? 'bg-slate-800 text-white hover:bg-slate-700 border border-slate-700'
                : 'bg-slate-800/50 text-slate-500 cursor-not-allowed border border-slate-800'
            }`}
            disabled={!isClickable}
            onClick={(e) => {
              e.stopPropagation();
              if (isClickable && onClick) onClick();
            }}
          >
            {isComplete ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Review
              </>
            ) : isCurrent ? (
              <>
                <Play className="w-4 h-4" />
                {status === 'inProgress' ? 'Continue Learning' : 'Start Learning'}
              </>
            ) : isClickable ? (
              <>
                <PlayCircle className="w-4 h-4" />
                Start Module
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Locked
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default EnhancedModuleCard;
